import {useContext, useEffect, useState} from 'react';

import {OrderListContext} from "../../context/OrderListContext";

import {count as countOrders} from "api/order/list";

import {isPeriodNeutralized} from "views/oms/OrderList/utils/isPeriodNeutralized";

import {LineItemStatus} from "utils/LineItemStatus";

import {STATUS_TABS} from "./statusTabsConfig";

function getStatusFromQueryParams(queryParams) {
    return new URLSearchParams(queryParams).get('status') ?? LineItemStatus.PENDING;
}

// the count is a per-status breakdown, so switching the selected status doesn't
// change it, stripping it keeps the count effect from refetching on every tab click
function withoutStatusParam(queryParams) {
    const params = new URLSearchParams(queryParams);

    params.delete('status');

    return params.toString();
}

// data/logic shared by the desktop StatusesTab and the mobile MobileStatusesTab -
// only their rendering differs
function useStatusTabs() {
    const {queryParams, setQueryParams, search} = useContext(OrderListContext);
    const countParams = withoutStatusParam(queryParams);

    const [loading, setLoading] = useState(false);
    const [statusSelected, setStatusSelected] = useState(() => getStatusFromQueryParams(queryParams));
    const [countRetrieved, setCountRetrieved] = useState({});

    function handleSelectStatus(status) {
        setStatusSelected(status);

        setQueryParams((previousQueryParams) => {
            const params = new URLSearchParams(previousQueryParams);

            params.set('status', status);
            params.set('page', '1');

            return params.toString();
        });
    }

    useEffect(() => {
        let cancelled = false;

        async function fetchCount() {
            setLoading(true);
            const retrieved = await countOrders(countParams);

            if (!cancelled) {
                setLoading(false);
                setCountRetrieved(retrieved);
            }
        }

        fetchCount();

        return () => {
            cancelled = true;
        };
    }, [countParams]);

    // a receipt value filter spans every date like a search, so it gets the same tab jump below
    const isSearching = !!search || isPeriodNeutralized(new URLSearchParams(queryParams));

    // while searching, jump to the first tab with results instead of leaving
    // the user stuck on a tab that has none - the search already spans every
    // status (countParams only strips "status"), so countRetrieved already
    // reflects, per tab, whether this search matches anything there
    useEffect(() => {
        if (!isSearching || loading) {
            return;
        }

        const currentTabCount = countRetrieved[STATUS_TABS.find((tab) => tab.status === statusSelected)?.countKey];

        if (currentTabCount) {
            return;
        }

        const firstTabWithResults = STATUS_TABS.find((tab) => countRetrieved[tab.countKey] > 0);

        if (firstTabWithResults && firstTabWithResults.status !== statusSelected) {
            handleSelectStatus(firstTabWithResults.status);
        }
    }, [isSearching, countRetrieved, loading]);

    return {statusSelected, countRetrieved, loading, handleSelectStatus};
}

export default useStatusTabs;
